import { useMemo, useState, useCallback } from "react";
import { Switch } from "antd";
import { CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";

import useApiMethods from "../../../lib/useApiMethods";
import ToastMsg from "../../../components/common/ToastMsg";
import { Button } from "../../../components/ui/button";
import { ConfirmPopupModal } from "../../../components/ui/dialogModal";
import { EMPLOYEE_MIGRATION_PROCESS } from "../../../constants/api";
import { FIELDS as DEFAULT_FIELDS, validateRow as defaultValidateRow } from "../migrationConfig";
import { WizardFooter, PRIMARY_BTN } from "./WizardFooter";
import MigrationGrid from "./MigrationGrid";

// Uniform sizing for every editable cell so the grid reads like one consistent
// form (same width + height for input / dropdown / date / password).
const COL_WIDTH = 190;
const ACTION_WIDTH = 56;
const BODY_HEIGHT = 460;

// Column descriptors for the virtualized grid (built once — all stable refs).
const GRID_COLUMNS = (fieldList) => [
    { key: "__select", title: "", width: 48, select: true },
    ...fieldList.map((field) => ({
        key: field.key,
        title: field.label,
        required: field.required,
        width: COL_WIDTH,
        field,
    })),
    { key: "__actions", title: "", width: ACTION_WIDTH, action: true },
];

const ReviewStep = ({
    rows,
    setRows,
    options,
    migrationId,
    counts,
    onProcessed,
    onNext,
    onCancel,
    fields = DEFAULT_FIELDS,
    validateRow = defaultValidateRow,
    processUrl = EMPLOYEE_MIGRATION_PROCESS,
    processExtra = {},
    recordsKey = "employee_data",
    optionSources,
    serializeRow,
}) => {
    const { update, fetch } = useApiMethods();
    const [processing, setProcessing] = useState(false);
    const [onlyConflicts, setOnlyConflicts] = useState(false);
    const [deleteRow, setDeleteRow] = useState(null);
    const [selectedKeys, setSelectedKeys] = useState(() => new Set());
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

    // Validity map keyed by stable row key — used only for the footer count and
    // the conflict filter. Cell-level errors are computed inside each row so
    // this never feeds the grid's render path.
    const invalidByKey = useMemo(() => {
        const map = {};
        for (const r of rows) map[r.__key] = Object.keys(validateRow(r, options)).length > 0;
        return map;
    }, [rows, options]);

    const clientInvalidCount = useMemo(
        () => Object.values(invalidByKey).filter(Boolean).length,
        [invalidByKey]
    );

    // Stable handler — identified by row key, so cell closures never change.
    const handleChange = useCallback(
        (rowKey, key, value) => {
            setRows((prev) => prev.map((r) => (r.__key === rowKey ? { ...r, [key]: value } : r)));
        },
        [setRows]
    );

    const confirmDelete = () => {
        setRows((prev) => prev.filter((r) => r.__key !== deleteRow.__key));
        setSelectedKeys((prev) => {
            if (!prev.has(deleteRow.__key)) return prev;
            const next = new Set(prev);
            next.delete(deleteRow.__key);
            return next;
        });
        setDeleteRow(null);
    };

    // --- row selection / bulk delete ----------------------------------------
    const toggleKey = useCallback((key) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const bulkDelete = () => {
        setRows((prev) => prev.filter((r) => !selectedKeys.has(r.__key)));
        setSelectedKeys(new Set());
        setBulkDeleteOpen(false);
    };

    const process = async () => {
        setProcessing(true);
        try {
            // Strip client-only bookkeeping; keep `id` so existing rows update.
            const payload = rows.map(({ __key, isError, ...rest }) =>
                serializeRow ? serializeRow(rest) : rest
            );
            const res = await update(processUrl, {
                data: payload,
                migration_id: migrationId,
                ...processExtra,
            });
            if (res?.status !== "success") {
                ToastMsg("error", res?.message || "Could not process records");
                return;
            }
            const id = res.migration_id;
            // Reload the stored batch: the server returns the conflict rows + counts.
            const detail = await fetch(processUrl, { migration_id: id, ...processExtra });
            const data = detail?.data || {};
            onProcessed(
                id,
                { processed: data.processed_records || 0, conflict: data.conflict_records || 0 },
                data[recordsKey] || []
            );
            ToastMsg("success", data.conflict_records
                ? `${data.conflict_records} record(s) still need attention`
                : "All records validated — ready to migrate");
        } catch (e) {
            ToastMsg("error", "Something went wrong while processing");
        } finally {
            setProcessing(false);
        }
    };

    const dataSource = onlyConflicts ? rows.filter((r) => invalidByKey[r.__key]) : rows;

    const selectedCount = useMemo(
        () => rows.reduce((n, r) => n + (selectedKeys.has(r.__key) ? 1 : 0), 0),
        [rows, selectedKeys]
    );
    const allVisibleSelected = dataSource.length > 0 && dataSource.every((r) => selectedKeys.has(r.__key));
    const someVisibleSelected = dataSource.some((r) => selectedKeys.has(r.__key));
    const toggleAll = (checked) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            dataSource.forEach((r) => (checked ? next.add(r.__key) : next.delete(r.__key)));
            return next;
        });
    };

    const allClean = migrationId && counts.conflict === 0 && counts.processed > 0;
    const nothingToShow = rows.length === 0;

    return (
        <div>
            <div className="p-4 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-4">
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                            Step 2 — Review &amp; fix ({rows.length})
                        </h3>
                        {migrationId && (
                            <>
                                <span className="flex items-center gap-1 text-xs text-green-600">
                                    <CheckCircle2 className="w-4 h-4" /> {counts.processed} processed
                                </span>
                                <span className="flex items-center gap-1 text-xs text-amber-600">
                                    <AlertTriangle className="w-4 h-4" /> {counts.conflict} conflicts
                                </span>
                            </>
                        )}
                    </div>
                    {rows.length > 0 && (
                        <div className="flex items-center gap-3">
                            {selectedCount > 0 && (
                                <Button
                                    variant="outline"
                                    onClick={() => setBulkDeleteOpen(true)}
                                    className="h-8 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:text-red-400"
                                >
                                    <Trash2 className="w-4 h-4 mr-1" /> Delete selected ({selectedCount})
                                </Button>
                            )}
                            <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Switch size="small" checked={onlyConflicts} onChange={setOnlyConflicts} />
                                Show only rows needing attention
                            </label>
                        </div>
                    )}
                </div>

                {nothingToShow ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <CheckCircle2 className="w-10 h-10 text-green-500 mb-3" />
                        <p className="text-sm text-gray-700 dark:text-gray-200">
                            {allClean
                                ? `${counts.processed} record(s) validated with no conflicts.`
                                : "No rows to review."}
                        </p>
                    </div>
                ) : (
                    <MigrationGrid
                        rows={dataSource}
                        columns={GRID_COLUMNS(fields)}
                        options={options}
                        validateRow={validateRow}
                        optionSources={optionSources}
                        onChange={handleChange}
                        onDelete={setDeleteRow}
                        selectedKeys={selectedKeys}
                        onToggleKey={toggleKey}
                        headerChecked={allVisibleSelected}
                        headerIndeterminate={someVisibleSelected && !allVisibleSelected}
                        onToggleAll={toggleAll}
                        height={BODY_HEIGHT}
                    />
                )}
            </div>

            <WizardFooter>
                <Button variant="outline" onClick={onCancel}>Cancel</Button>
                {rows.length > 0 && (
                    <Button
                        className={PRIMARY_BTN}
                        onClick={process}
                        disabled={processing || clientInvalidCount > 0}
                    >
                        {processing ? "Processing…" : "Process & Validate"}
                    </Button>
                )}
                <Button
                    className={PRIMARY_BTN}
                    onClick={onNext}
                    disabled={!allClean}
                    title={!allClean ? "Process all records without conflicts first" : ""}
                >
                    Next: Migrate
                </Button>
            </WizardFooter>

            {deleteRow && (
                <ConfirmPopupModal
                    open
                    onClose={() => setDeleteRow(null)}
                    onConfirm={confirmDelete}
                    title="Remove this row?"
                    description="This row will be excluded from the migration."
                    confirmLabel="Remove"
                    destructive
                />
            )}

            <ConfirmPopupModal
                open={bulkDeleteOpen}
                onClose={() => setBulkDeleteOpen(false)}
                onConfirm={bulkDelete}
                title={`Remove ${selectedCount} selected row(s)?`}
                description="The selected rows will be excluded from the migration."
                confirmLabel="Remove"
                destructive
            />
        </div>
    );
};

export default ReviewStep;
